import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import CategoryMultiSelect from "./CategoryMultiSelect";

type Interest =
	| "מוצרי חשמל ביתיים"
	| "אלקטרוניקה וגאדג'טים"
	| "רהיטים ונגרות"
	| "רכב ואופניים"
	| "מחשבים וטלפונים"
	| "אינסטלציה"
	| "צבע ושיפוצים קלים";

const ALL_INTERESTS: Interest[] = [
	"מוצרי חשמל ביתיים",
	"אלקטרוניקה וגאדג'טים",
	"רהיטים ונגרות",
	"רכב ואופניים",
	"מחשבים וטלפונים",
	"אינסטלציה",
	"צבע ושיפוצים קלים",
];

const LEVEL_LABELS = [
	"מתחיל מוחלט",
	"חובבן",
	"מתקדם",
	"מנוסה",
	"סמי-פרו",
	"איש מקצוע",
];

export interface OnboardingData {
	interests: Interest[];
	level: number; // 0..5
	heardFrom:
		| "גוגל"
		| "פייסבוק"
		| "אינסטגרם"
		| "יוטיוב"
		| "וואטסאפ"
		| "פה לאוזן"
		| "אחר";
	otherSource?: string;
	goalsText?: string;
	followCategories?: string[]; // category ids
	notifyCategoryPosts?: boolean;
}

interface Props {
	isOpen: boolean;
	onClose: () => void; // for "אח"כ"
	onComplete: (data: OnboardingData) => Promise<void> | void;
}

const OnboardingModal: React.FC<Props> = ({ isOpen, onClose, onComplete }) => {
	const [interests, setInterests] = useState<Interest[]>([]);
	const [level, setLevel] = useState<number>(0);
	const [heardFrom, setHeardFrom] = useState<OnboardingData["heardFrom"] | "">(
		""
	);
	const [otherSource, setOtherSource] = useState<string>("");
	const [goalsText, setGoalsText] = useState<string>("");
	const [followCategories, setFollowCategories] = useState<string[]>([]);
	const [notifyCategoryPosts, setNotifyCategoryPosts] = useState<boolean>(true);
	const [submitting, setSubmitting] = useState(false);
	const [errorMsg, setErrorMsg] = useState<string>("");

	useEffect(() => {
		if (!isOpen) {
			// reset when closed
			setInterests([]);
			setLevel(0);
			setHeardFrom("");
			setOtherSource("");
			setGoalsText("");
			setFollowCategories([]);
			setNotifyCategoryPosts(true);
			setSubmitting(false);
		}
	}, [isOpen]);

	const isValid = useMemo(() => {
		const hasInterests = interests.length > 0;
		const hasSource =
			heardFrom !== "" &&
			(heardFrom !== "אחר" || otherSource.trim().length > 1);
		return hasInterests && hasSource;
	}, [interests, heardFrom, otherSource]);

	const toggleInterest = (i: Interest) => {
		setInterests((prev) =>
			prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
		);
	};

	const submit = async () => {
		if (!isValid) return;
		setSubmitting(true);
		try {
			await onComplete({
				interests,
				level,
				heardFrom: heardFrom as OnboardingData["heardFrom"],
				otherSource:
					heardFrom === "אחר" && otherSource.trim().length > 1
						? otherSource.trim()
						: null,
				goalsText: goalsText.trim() || undefined,
				followCategories,
				notifyCategoryPosts,
			});
		} finally {
			setSubmitting(false);
		}
	};

	const explainInvalid = (): string => {
		const reasons: string[] = [];
		if (interests.length === 0) reasons.push("בחר לפחות תחום עניין אחד");
		if (heardFrom === "") reasons.push("בחר איך שמעת עלינו");
		if (heardFrom === "אחר" && otherSource.trim().length <= 1)
			reasons.push("הזן מקור אחר");
		return reasons.join(" • ");
	};

	const attemptSubmit = async () => {
		if (!isValid) {
			const msg = explainInvalid();
			setErrorMsg(msg);
			try {
				const el = document.getElementById("onboarding-error");
				el?.scrollIntoView({ behavior: "smooth", block: "start" });
			} catch {}
			return;
		}
		setErrorMsg("");
		await submit();
	};

	if (!isOpen) return null;

	const modal = (
		<div className="fixed inset-0 z-[10000] flex items-center justify-center">
			<div className="absolute inset-0 bg-black/60" />
			<div className="relative w-full max-w-2xl max-w-[95vw] mx-4 rounded-xl bg-slate-900 border border-slate-700 shadow-2xl max-h-[90vh] overflow-auto">
				<div className="p-6">
					<h2 className="text-2xl font-bold text-white mb-2 text-center">
						ברוך הבא! נכיר אותך קצת
					</h2>
					<p className="text-slate-300 text-center mb-6">
						ספר לנו על תחומי העניין ורמת הניסיון שלך כדי שנתאים לך תוכן טוב
						יותר.
					</p>

					{/* Top action bar for long content */}
					<div className="sticky top-0 z-10 -mx-6 px-6 py-3 bg-slate-900/95 backdrop-blur border-b border-slate-700 flex items-center justify-end gap-2 mb-4">
						{errorMsg && (
							<div
								id="onboarding-error"
								className="mr-auto text-xs text-red-400"
							>
								{errorMsg}
							</div>
						)}
						<button
							onClick={onClose}
							className="px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm"
						>
							אח"כ
						</button>
						<button
							disabled={!isValid || submitting}
							onClick={attemptSubmit}
							className={`px-3 py-1.5 rounded-md font-semibold text-sm ${
								!isValid || submitting
									? "bg-cyan-500/40 cursor-not-allowed text-slate-900"
									: "bg-cyan-400 hover:bg-cyan-300 text-slate-900"
							}`}
						>
							סיום
						</button>
					</div>

					{/* Interests */}
					<div className="mb-6">
						<h3 className="text-lg font-semibold text-cyan-400 mb-3">
							תחומי עניין בתיקון ושיפור
						</h3>
						<div className="flex flex-wrap gap-2">
							{ALL_INTERESTS.map((i) => {
								const active = interests.includes(i);
								return (
									<button
										key={i}
										onClick={() => toggleInterest(i)}
										className={
											"px-3 py-2 rounded-full border text-sm " +
											(active
												? "bg-cyan-500/20 border-cyan-400 text-cyan-200"
												: "bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700")
										}
									>
										{i}
									</button>
								);
							})}
						</div>
					</div>

					{/* Level slider */}
					<div className="mb-6">
						<h3 className="text-lg font-semibold text-cyan-400 mb-3">
							רמת ניסיון
						</h3>
						<div className="flex items-center gap-3">
							<span className="text-slate-400 text-sm">מתחיל</span>
							<input
								type="range"
								min={0}
								max={5}
								step={1}
								value={level}
								onChange={(e) => setLevel(parseInt(e.target.value))}
								className="flex-1 accent-cyan-400"
							/>
							<span className="text-slate-400 text-sm">איש מקצוע</span>
						</div>
						<div className="mt-2 text-center text-slate-300 text-sm">
							{LEVEL_LABELS[level]}
						</div>
					</div>

					{/* Heard from */}
					<div className="mb-6">
						<h3 className="text-lg font-semibold text-cyan-400 mb-3">
							איך שמעת עלינו?
						</h3>
						<div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
							{[
								"גוגל",
								"פייסבוק",
								"אינסטגרם",
								"יוטיוב",
								"וואטסאפ",
								"פה לאוזן",
								"אחר",
							].map((opt) => (
								<button
									key={opt}
									onClick={() => setHeardFrom(opt as any)}
									className={
										"px-3 py-2 rounded-md border text-sm " +
										(heardFrom === opt
											? "bg-cyan-500/20 border-cyan-400 text-cyan-200"
											: "bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700")
									}
								>
									{opt}
								</button>
							))}
						</div>
						{heardFrom === "אחר" && (
							<input
								type="text"
								value={otherSource}
								onChange={(e) => setOtherSource(e.target.value)}
								placeholder="מאיפה שמעת?"
								className="mt-2 w-full rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-slate-100 placeholder-slate-400"
							/>
						)}
					</div>

					{/* Goals */}
					<div className="mb-6">
						<h3 className="text-lg font-semibold text-cyan-400 mb-3">
							מטרות אישיות קצרות
						</h3>
						<textarea
							value={goalsText}
							onChange={(e) => setGoalsText(e.target.value)}
							maxLength={200}
							placeholder="מה היית רוצה ללמוד/לתקן בחודש הקרוב?"
							className="w-full rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-slate-100 placeholder-slate-400 min-h-20"
						/>
						<div className="text-xs text-slate-400 mt-1">
							{goalsText.length}/200
						</div>
					</div>

					{/* Follow categories opt-in (basic input, suggest editing later to dynamic) */}
					<div className="mb-6">
						<h3 className="text-lg font-semibold text-cyan-400 mb-2">
							עקוב אחרי קטגוריות
						</h3>
						<p className="text-slate-400 text-sm mb-2">
							בחר קטגוריות שתרצה לקבל מהן עדכונים על פוסטים חדשים. ניתן לשנות
							בפרופיל.
						</p>
						<CategoryMultiSelect
							selectedIds={followCategories}
							onChange={setFollowCategories}
						/>
						<label className="flex items-center gap-2 mt-2 text-slate-300 text-sm">
							<input
								type="checkbox"
								checked={notifyCategoryPosts}
								onChange={(e) => setNotifyCategoryPosts(e.target.checked)}
								className="accent-cyan-400"
							/>
							<span>קבל הודעות על פוסטים חדשים בקטגוריות אלו</span>
						</label>
					</div>

					<div className="flex items-center justify-between gap-3">
						<button
							onClick={onClose}
							className="px-4 py-2 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-100"
						>
							אח"כ
						</button>
						<button
							disabled={!isValid || submitting}
							onClick={attemptSubmit}
							className={`px-4 py-2 rounded-md font-semibold ${
								!isValid || submitting
									? "bg-cyan-500/40 cursor-not-allowed text-slate-900"
									: "bg-cyan-400 hover:bg-cyan-300 text-slate-900"
							}`}
						>
							סיום
						</button>
					</div>
				</div>
			</div>
		</div>
	);

	return typeof document !== "undefined"
		? ReactDOM.createPortal(modal, document.body)
		: modal;
};

export default OnboardingModal;
